<template>
  <div class="conv-list">
    <div class="conv-list__head">
      <h2 class="conv-list__title">{{ $t("messages.title") }}</h2>
      <div class="conv-list__tabs" role="tablist">
        <button
          v-for="item in tabs"
          :key="item.key"
          type="button"
          role="tab"
          class="conv-list__tab"
          :class="{ 'conv-list__tab--active': tab === item.key }"
          :aria-selected="tab === item.key ? 'true' : 'false'"
          @click="$emit('update:tab', item.key)"
        >
          <v-icon small class="mr-1">{{ item.icon }}</v-icon>
          {{ item.label }}
          <span v-if="unread[item.key] > 0" class="conv-list__tab-badge">
            {{ unread[item.key] > 99 ? "99+" : unread[item.key] }}
          </span>
        </button>
      </div>

      <v-text-field
        :value="search"
        dense
        outlined
        hide-details
        clearable
        prepend-inner-icon="mdi-magnify"
        :placeholder="
          tab === 'lessons'
            ? $t('messages.searchLessons')
            : $t('messages.search')
        "
        class="conv-list__search"
        @input="$emit('update:search', $event || '')"
      />
    </div>

    <div v-if="tab === 'support'" class="conv-list__support-hint">
      <p class="mb-2">
        {{ $t("messages.supportHint") }}
      </p>
      <v-btn
        v-if="whatsappLink"
        :href="whatsappLink"
        target="_blank"
        rel="noopener noreferrer"
        small
        depressed
        color="#25D366"
        class="white--text"
      >
        <v-icon small left>mdi-whatsapp</v-icon>
        {{ $t("messages.whatsapp") }}
      </v-btn>
    </div>

    <div class="conv-list__items">
      <page-skeleton
        v-if="loading && !items.length"
        variant="list"
        bare
        :rows="4"
        class="pa-2"
      />

      <div v-else-if="!items.length" class="conv-list__empty">
        <v-icon large color="grey lighten-1">mdi-forum-outline</v-icon>
        <p v-if="search">{{ $t("messages.notFound", { term: search }) }}</p>
        <p v-else-if="tab === 'lessons'">
          {{
            isTeacher
              ? $t("messages.emptyTeacher")
              : $t("messages.emptyStudent")
          }}
        </p>
      </div>

      <button
        v-for="item in items"
        :key="itemKey(item)"
        type="button"
        class="conv-item"
        :class="{
          'conv-item--active': isActive(item),
          'conv-item--unread': item.unread > 0,
        }"
        @click="$emit('select', item)"
      >
        <span
          class="conv-item__avatar"
          :class="`conv-item__avatar--${item.type}`"
        >
          <img v-if="item.avatar" :src="item.avatar" alt="" />
          <v-icon v-else-if="item.type === 'group'" color="white">
            mdi-account-group
          </v-icon>
          <v-icon v-else-if="item.type === 'support'" color="white">
            mdi-lifebuoy
          </v-icon>
          <span v-else>{{ item.initials }}</span>
        </span>

        <span class="conv-item__body">
          <span class="conv-item__row">
            <span class="conv-item__name">{{ item.title }}</span>
            <span class="conv-item__time">
              {{ formatTime(item.lastMessageAt) }}
            </span>
          </span>
          <span class="conv-item__row">
            <span class="conv-item__preview">
              <template v-if="item.lastMessage">
                <span v-if="item.lastMessage.mine"
                  >{{ $t("messages.you") }}
                </span>
                <span v-else-if="item.lastMessage.authorName">
                  {{ item.lastMessage.authorName }}:
                </span>
                <v-icon v-if="item.lastMessage.hasImage" x-small>
                  mdi-image-outline
                </v-icon>
                {{ item.lastMessage.preview }}
              </template>
              <template v-else>{{ item.subtitle }}</template>
            </span>
            <span v-if="item.unread > 0" class="conv-item__badge">
              {{ item.unread > 99 ? "99+" : item.unread }}
            </span>
          </span>
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import { formatConversationTime } from "@/shared/messagesTime";
import { conversationKey } from "@/shared/messagesKeys";

export default {
  name: "ConversationList",
  props: {
    items: { type: Array, default: () => [] },
    activeKey: { type: String, default: null },
    tab: { type: String, default: "lessons" },
    search: { type: String, default: "" },
    unread: {
      type: Object,
      default: () => ({ lessons: 0, support: 0, total: 0 }),
    },
    loading: { type: Boolean, default: false },
    whatsappLink: { type: String, default: null },
    isTeacher: { type: Boolean, default: false },
  },
  computed: {
    tabs() {
      return [
        {
          key: "lessons",
          label: this.$t("messages.tabLessons"),
          icon: "mdi-school-outline",
        },
        {
          key: "support",
          label: this.$t("messages.tabSupport"),
          icon: "mdi-lifebuoy",
        },
      ];
    },
  },
  methods: {
    formatTime: formatConversationTime,
    itemKey(item) {
      return conversationKey(item);
    },
    isActive(item) {
      return this.activeKey && conversationKey(item) === this.activeKey;
    },
  },
};
</script>

<style scoped>
.conv-list {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: var(--dk-surface, #fff);
}
.conv-list__head {
  padding: 16px 16px 8px;
  border-bottom: 1px solid rgba(var(--dk-ink-rgb, 35, 26, 46), 0.08);
}
.conv-list__title {
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--dk-ink, #231a2e);
  margin-bottom: 12px;
}
.conv-list__tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}
.conv-list__tab {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.85rem;
  color: var(--dk-muted, #6b6472);
  background: var(--dk-paper, #f4f0f5);
  transition: background 0.15s ease, color 0.15s ease;
}
.conv-list__tab .v-icon {
  color: inherit;
}
.conv-list__tab--active {
  background: var(--msg-accent);
  color: #fff;
}
.conv-list__tab-badge {
  margin-left: 6px;
  min-width: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--dk-red, #e53935);
  color: #fff;
  font-size: 0.72rem;
  line-height: 20px;
}
.conv-list__search {
  margin-bottom: 4px;
}
.conv-list__support-hint {
  padding: 12px 16px;
  font-size: 0.85rem;
  color: var(--dk-muted, #6b6472);
  background: var(--msg-accent-soft);
}
.conv-list__items {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
}
.conv-list__empty {
  padding: 32px 24px;
  text-align: center;
  color: var(--dk-muted, #6b6472);
  font-size: 0.9rem;
}
.conv-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 16px;
  text-align: left;
  border-bottom: 1px solid rgba(var(--dk-ink-rgb, 35, 26, 46), 0.05);
  transition: background 0.12s ease;
}
.conv-item:hover {
  background: #faf7fb;
}
.conv-item--active {
  background: var(--msg-accent-soft) !important;
  box-shadow: inset 3px 0 0 var(--msg-accent);
}
.conv-item__avatar {
  flex: 0 0 46px;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  overflow: hidden;
  background: var(--msg-accent);
  color: #fff;
  font-weight: 800;
}
.conv-item__avatar--group {
  background: #5c6bc0;
}
.conv-item__avatar--support {
  background: #26a69a;
}
.conv-item__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.conv-item__body {
  flex: 1 1 auto;
  min-width: 0;
}
.conv-item__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.conv-item__name {
  font-weight: 700;
  color: var(--dk-ink, #231a2e);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.conv-item__time {
  flex: 0 0 auto;
  font-size: 0.72rem;
  color: var(--dk-muted, #8a8490);
}
.conv-item__preview {
  font-size: 0.84rem;
  color: var(--dk-muted, #6b6472);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.conv-item--unread .conv-item__preview,
.conv-item--unread .conv-item__time {
  color: var(--dk-ink, #231a2e);
  font-weight: 700;
}
.conv-item--unread .conv-item__time {
  color: var(--msg-accent);
}
.conv-item__badge {
  flex: 0 0 auto;
  min-width: 22px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--msg-accent);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 22px;
  text-align: center;
}
</style>
